import React, { useState } from 'react';
import { Info, Layers, Package, Palette, FolderOpen, Terminal, Brush } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div className="card">
    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-navy-700">
      <span className="text-maritime-blue">{icon}</span>
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
    </div>
    {children}
  </div>
);

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start justify-between py-2 border-b border-navy-800 last:border-0">
    <span className="text-sm text-navy-400 w-36 shrink-0">{label}</span>
    <span className="text-sm text-foreground text-right">{value}</span>
  </div>
);

const Badge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-block bg-maritime-blue/20 text-maritime-blue text-xs font-mono px-2 py-0.5 rounded">
    {children}
  </span>
);

/* ── Tailwind 클래스 데이터 ── */

const navyColors = [
  { cls: 'bg-navy-950', hex: '#0a1929', label: '950 — 최심 배경 (body)' },
  { cls: 'bg-navy-900', hex: '#102a43', label: '900 — 사이드바 / 헤더 배경' },
  { cls: 'bg-navy-800', hex: '#243b53', label: '800 — 카드 배경 (.card)' },
  { cls: 'bg-navy-700', hex: '#334e68', label: '700 — 테두리 / 구분선' },
  { cls: 'bg-navy-600', hex: '#486581', label: '600 — 비활성 보더 / hover' },
  { cls: 'bg-navy-500', hex: '#627d98', label: '500 — 보조 텍스트' },
  { cls: 'bg-navy-400', hex: '#829ab1', label: '400 — placeholder / 부제목' },
  { cls: 'bg-navy-300', hex: '#9fb3c8', label: '300 — 아이콘 / 비활성 텍스트' },
  { cls: 'bg-navy-200', hex: '#bcccdc', label: '200 — 라벨' },
  { cls: 'bg-navy-100', hex: '#d9e2ec', label: '100 — 연한 텍스트' },
  { cls: 'bg-navy-50',  hex: '#f0f4f8', label: '50 — 최밝은 navy' },
];

const maritimeColors = [
  { cls: 'bg-maritime-blue',   hex: '#0077b6', label: 'blue — 주 액센트 / 활성 메뉴 / 버튼' },
  { cls: 'bg-maritime-light',  hex: '#00b4d8', label: 'light — 밝은 강조' },
  { cls: 'bg-maritime-accent', hex: '#48cae4', label: 'accent — 차트 포인트' },
  { cls: 'bg-maritime-sea',    hex: '#023e8a', label: 'sea — btn-primary hover' },
  { cls: 'bg-maritime-dark',   hex: '#03045e', label: 'dark — 가장 진한 해양 색' },
];

const componentClasses = [
  { cls: '.card',              desc: 'bg-navy-800 rounded-xl border border-navy-700 p-6 shadow-lg — 기본 카드 컨테이너' },
  { cls: '.card-header',       desc: 'text-lg font-semibold text-foreground mb-4 — 카드 제목' },
  { cls: '.btn-primary',       desc: 'bg-maritime-blue hover:bg-maritime-sea text-white px-4 py-2 rounded-lg — 주요 버튼' },
  { cls: '.btn-secondary',     desc: 'bg-navy-700 hover:bg-navy-600 text-foreground border border-navy-600 — 보조 버튼' },
  { cls: '.sidebar-link',      desc: 'flex items-center gap-3 px-4 py-3 text-navy-300 rounded-lg — 비활성 사이드바 메뉴' },
  { cls: '.sidebar-link-active', desc: 'text-foreground bg-maritime-blue/20 border-r-2 border-maritime-blue — 활성 사이드바 메뉴' },
  { cls: '.table-header',      desc: 'bg-navy-800 text-navy-300 text-sm uppercase tracking-wider — 테이블 헤더 셀' },
  { cls: '.table-row',         desc: 'border-b border-navy-700 transition-colors hover:bg-white/3 — 테이블 행' },
  { cls: '.badge',             desc: 'px-2.5 py-0.5 rounded-full text-xs font-medium — 기본 뱃지 (베이스)' },
  { cls: '.badge-success',     desc: 'bg-green-500/20 text-green-400 — 성공 / 운항중 / 정상' },
  { cls: '.badge-warning',     desc: 'bg-yellow-500/20 text-yellow-400 — 경고 / 점검필요' },
  { cls: '.badge-danger',      desc: 'bg-red-500/20 text-red-400 — 위험 / 긴급 / 정박' },
  { cls: '.badge-info',        desc: 'bg-blue-500/20 text-blue-400 — 정보 / 진행중' },
  { cls: '.badge-secondary',   desc: 'bg-navy-600/50 text-navy-300 — 비활성 / 일반 상태' },
  { cls: '.input-field',       desc: 'bg-navy-900 border border-navy-600 rounded-lg px-4 py-2.5 focus:ring-maritime-blue — 입력 필드' },
  { cls: '.select-field',      desc: 'bg-navy-900 border border-navy-600 rounded-lg px-4 py-2.5 focus:ring-maritime-blue — 셀렉트 박스' },
];

const TABS = ['커스텀 색상', '컴포넌트 클래스'] as const;
type Tab = typeof TABS[number];

const SystemInfoPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('커스텀 색상');

  return (
    <div className="page-container">
      <PageHeader
        title="시스템 정보"
        subtitle="선박통합관리시스템 기술 스펙 및 구성 정보"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* 프로젝트 개요 */}
        <Section icon={<Info size={18} />} title="프로젝트 개요">
          <Row label="시스템명" value="선박통합관리시스템" />
          <Row label="영문명" value="Ship Management System" />
          <Row label="버전" value={<Badge>v1.0.0</Badge>} />
          <Row label="데이터 방식" value="Mock 데이터 (프론트엔드 전용)" />
          <Row label="언어" value="한국어 UI" />
        </Section>

        {/* 기술 스택 */}
        <Section icon={<Layers size={18} />} title="기술 스택">
          <Row label="프레임워크" value={<Badge>React 19</Badge>} />
          <Row label="언어" value={<Badge>TypeScript</Badge>} />
          <Row label="스타일" value={<Badge>Tailwind CSS v3</Badge>} />
          <Row label="라우팅" value={<Badge>react-router-dom v7</Badge>} />
          <Row label="차트" value={<Badge>recharts v3</Badge>} />
          <Row label="아이콘" value={<Badge>lucide-react</Badge>} />
          <Row label="날짜" value={<Badge>date-fns (한국어)</Badge>} />
          <Row label="빌드" value={<Badge>Create React App</Badge>} />
        </Section>

        {/* 디자인 시스템 */}
        <Section icon={<Palette size={18} />} title="디자인 시스템">
          <Row label="배경색" value={
            <span className="flex items-center gap-2">
              <span className="inline-block w-4 h-4 rounded border border-navy-600" style={{ backgroundColor: '#0a1929' }} />
              <Badge>#0a1929</Badge>
            </span>
          } />
          <Row label="액센트" value={
            <span className="flex items-center gap-2">
              <span className="inline-block w-4 h-4 rounded" style={{ backgroundColor: '#0077b6' }} />
              <Badge>#0077b6</Badge>
            </span>
          } />
          <Row label="폰트" value="Pretendard (한국어)" />
          <Row label="사이드바" value="고정 280px / 모바일 드로어" />
          <Row label="레이아웃" value="카드 기반, 반응형" />
        </Section>

        {/* 라이브러리 */}
        <Section icon={<Package size={18} />} title="설치 패키지">
          <Row label="react" value={<Badge>^19.0.0</Badge>} />
          <Row label="react-router-dom" value={<Badge>^7.0.0</Badge>} />
          <Row label="recharts" value={<Badge>^3.0.0</Badge>} />
          <Row label="lucide-react" value={<Badge>latest</Badge>} />
          <Row label="date-fns" value={<Badge>latest</Badge>} />
          <Row label="tailwindcss" value={<Badge>^3.0.0</Badge>} />
        </Section>

        {/* 폴더 구조 */}
        <Section icon={<FolderOpen size={18} />} title="폴더 구조">
          {[
            { path: 'src/types/', desc: '타입 정의 (6개)' },
            { path: 'src/data/', desc: 'Mock 데이터 (6개)' },
            { path: 'src/utils/', desc: 'format.ts, constants.ts' },
            { path: 'src/hooks/', desc: 'useSearch, usePagination' },
            { path: 'src/components/layout/', desc: 'Layout, Sidebar, Header, Breadcrumb' },
            { path: 'src/components/common/', desc: '공통 컴포넌트 (6개)' },
            { path: 'src/components/charts/', desc: '차트 컴포넌트 (4개)' },
            { path: 'src/pages/', desc: '페이지 컴포넌트 (13개)' },
            { path: 'src/routes/', desc: 'AppRoutes.tsx' },
          ].map(item => (
            <div key={item.path} className="flex items-start justify-between py-2 border-b border-navy-800 last:border-0">
              <code className="text-xs text-maritime-blue font-mono">{item.path}</code>
              <span className="text-xs text-navy-400 text-right">{item.desc}</span>
            </div>
          ))}
        </Section>

        {/* 라우트 */}
        <Section icon={<Terminal size={18} />} title="라우트 구성 (16개)">
          {[
            { path: '/', desc: '→ /dashboard 리다이렉트' },
            { path: '/dashboard', desc: '대시보드' },
            { path: '/ships', desc: '선박 목록' },
            { path: '/ships/register', desc: '선박 등록' },
            { path: '/ships/:id', desc: '선박 상세' },
            { path: '/voyages', desc: '운항 목록' },
            { path: '/voyages/schedule', desc: '운항 일정' },
            { path: '/voyages/:id', desc: '운항 상세' },
            { path: '/maintenance', desc: '정비 목록' },
            { path: '/maintenance/work-order', desc: '작업 지시' },
            { path: '/maintenance/:id', desc: '정비 상세' },
            { path: '/crew', desc: '선원 목록' },
            { path: '/crew/assignment', desc: '선원 배정' },
            { path: '/crew/:id', desc: '선원 상세' },
            { path: '/alerts', desc: '알림' },
            { path: '/notices', desc: '공지사항' },
          ].map(item => (
            <div key={item.path} className="flex items-start justify-between py-2 border-b border-navy-800 last:border-0">
              <code className="text-xs text-maritime-blue font-mono">{item.path}</code>
              <span className="text-xs text-navy-400">{item.desc}</span>
            </div>
          ))}
        </Section>

      </div>

      {/* 개발 명령어 */}
      <div className="card mt-6">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-navy-700">
          <span className="text-maritime-blue"><Terminal size={18} /></span>
          <h2 className="text-base font-semibold text-foreground">개발 명령어</h2>
        </div>
        <div className="flex flex-wrap gap-4">
          {[
            { cmd: 'npm start', desc: '개발 서버 실행' },
            { cmd: 'npm run build', desc: '프로덕션 빌드' },
            { cmd: 'npm test', desc: '테스트 실행' },
          ].map(item => (
            <div key={item.cmd} className="bg-navy-900 rounded-lg px-4 py-3 flex items-center gap-3">
              <code className="text-maritime-blue font-mono text-sm">{item.cmd}</code>
              <span className="text-navy-400 text-xs">{item.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tailwind 클래스 레퍼런스 */}
      <div className="card mt-6">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-navy-700">
          <span className="text-maritime-blue"><Brush size={18} /></span>
          <h2 className="text-base font-semibold text-foreground">Tailwind 클래스 레퍼런스</h2>
        </div>

        {/* 탭 */}
        <div className="flex gap-2 mb-5">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-maritime-blue text-white'
                  : 'bg-navy-700 text-navy-300 hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* 커스텀 색상 탭 */}
        {activeTab === '커스텀 색상' && (
          <div className="space-y-6">
            {/* Navy 팔레트 */}
            <div>
              <p className="text-xs text-navy-400 mb-3 font-medium uppercase tracking-wider">Navy 팔레트 (CSS 변수 기반, 다크/라이트 반응형)</p>
              <div className="space-y-1.5">
                {navyColors.map(({ cls, hex, label }) => (
                  <div key={cls} className="flex items-center gap-3">
                    <div className={`${cls} w-8 h-8 rounded-md border border-navy-600 shrink-0`} />
                    <code className="text-xs font-mono text-maritime-blue w-28 shrink-0">{cls}</code>
                    <span className="text-xs text-navy-400 font-mono w-20 shrink-0">{hex}</span>
                    <span className="text-xs text-navy-300">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Maritime 팔레트 */}
            <div>
              <p className="text-xs text-navy-400 mb-3 font-medium uppercase tracking-wider">Maritime 팔레트 (고정 색상)</p>
              <div className="space-y-1.5">
                {maritimeColors.map(({ cls, hex, label }) => (
                  <div key={cls} className="flex items-center gap-3">
                    <div className={`${cls} w-8 h-8 rounded-md border border-navy-600 shrink-0`} />
                    <code className="text-xs font-mono text-maritime-blue w-36 shrink-0">{cls}</code>
                    <span className="text-xs text-navy-400 font-mono w-20 shrink-0">{hex}</span>
                    <span className="text-xs text-navy-300">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* foreground */}
            <div>
              <p className="text-xs text-navy-400 mb-3 font-medium uppercase tracking-wider">기타</p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-md border border-navy-600 shrink-0 bg-foreground" />
                <code className="text-xs font-mono text-maritime-blue w-28 shrink-0">text-foreground</code>
                <span className="text-xs text-navy-400 font-mono w-20 shrink-0">CSS 변수</span>
                <span className="text-xs text-navy-300">다크: #fff / 라이트: #0f172a — 기본 텍스트 색상</span>
              </div>
            </div>
          </div>
        )}

        {/* 컴포넌트 클래스 탭 */}
        {activeTab === '컴포넌트 클래스' && (
          <div className="space-y-1">
            <p className="text-xs text-navy-400 mb-3">
              <code className="text-maritime-blue">index.css</code> 의 <code className="text-maritime-blue">@layer components</code>에 정의된 공통 클래스
            </p>
            {componentClasses.map(({ cls, desc }) => (
              <div key={cls} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2.5 border-b border-navy-800 last:border-0">
                <code className="text-xs font-mono text-maritime-blue shrink-0 w-44">{cls}</code>
                <span className="text-xs text-navy-300 leading-relaxed">{desc}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemInfoPage;
